<template>
  <div class="column-oral studio-layout">
    <!-- 配置面板 -->
    <el-card>
      <template #header>
        <div class="card-header">
          <span>专栏口播配置</span>
          <el-tag type="info" size="small">
            固定平台：专栏 · 独立提示词 · 输出结构化 references
          </el-tag>
        </div>
      </template>

      <el-form :model="form" label-width="120px">
        <el-form-item label="主题" required>
          <el-input
            v-model="form.topic"
            type="textarea"
            :rows="3"
            placeholder="例：网红教育博主 / 短剧 / 公益广告 / 流行歌曲 ……（决定一整批口播稿要讨论的主题对象）"
          />
        </el-form-item>

        <el-form-item label="理论资源">
          <el-input
            v-model="form.theory"
            placeholder="可选。例：本雅明 + 阿多诺 / 葛兰西 + 鲁迅"
          />
        </el-form-item>

        <el-form-item label="案例素材">
          <el-input
            v-model="form.cases"
            placeholder="可选。指定要分析的影视/广告/综艺/IP/事件，留空则由模型按主题自选"
          />
        </el-form-item>

        <el-form-item label="补充说明">
          <el-input
            v-model="form.extraHint"
            type="textarea"
            :rows="2"
            placeholder="可选：拆方向 / 写正文时的额外约束（结尾基调 / 必须避免的角度等）"
          />
        </el-form-item>

        <el-form-item label="写正文检索">
          <el-switch
            v-model="form.runSearch"
            active-text="联网 WebSearch"
            inactive-text="不检索"
          />
          <el-input-number
            v-model="form.searchBudget"
            :min="3"
            :max="12"
            :step="1"
            size="small"
            style="margin-left: 16px"
            :disabled="!form.runSearch"
          />
          <span class="hint-text" style="margin-left: 8px">条素材 / 每篇</span>
          <p class="field-hint">
            拆方向不联网（速度优先，约 10-20 秒）；写正文阶段按本开关决定是否检索素材并自动塞入 references 候选
          </p>
        </el-form-item>

        <el-form-item label=" ">
          <el-button
            type="primary"
            :loading="loadingDirections"
            :disabled="!form.topic.trim()"
            @click="fetchDirections"
          >
            AI 拆方向
          </el-button>
          <el-button :icon="Plus" @click="addCustomDirection">添加自定义方向</el-button>
          <span class="hint-text">也可不点生成，直接手写方向后勾选</span>
        </el-form-item>

        <el-form-item label="写正文模板">
          <el-select
            v-model="form.articlePromptKey"
            placeholder="选择写正文 System 提示词"
            style="min-width: 240px"
            :loading="loadingArticlePrompts"
          >
            <el-option
              v-for="opt in articlePromptOptions"
              :key="opt.key || '__default__'"
              :label="opt.name"
              :value="opt.key"
            >
              <span style="float: left">{{ opt.name }}</span>
              <span style="float: right; color: #909399; font-size: 12px; margin-left: 8px">
                {{ opt.is_default ? '默认' : '自定义' }}
              </span>
            </el-option>
          </el-select>
          <el-button link type="primary" :icon="Refresh" @click="loadArticlePrompts">刷新</el-button>
          <span class="hint-text">在「提示词模板 · 专栏口播」处可新增/编辑变体</span>
        </el-form-item>

        <el-form-item label=" ">
          <el-button
            type="primary"
            size="large"
            :loading="generating"
            :disabled="selectedCount === 0 || !form.topic.trim()"
            @click="startGenerate"
          >
            生成口播稿（{{ selectedCount }} 篇）
          </el-button>
          <el-button size="large" @click="resetForm">重置</el-button>
          <span v-if="selectedCount > 0" class="count-hint">将生成 {{ selectedCount }} 篇专栏口播稿</span>
        </el-form-item>
      </el-form>

      <el-progress
        v-if="genJob.status === 'running' || genJob.status === 'pending'"
        :percentage="genProgress"
        :stroke-width="10"
        style="margin-top: 4px"
      />
    </el-card>

    <!-- 批评方向列表（表格） -->
    <el-card class="directions-card">
      <template #header>
        <div class="card-header">
          <div class="preview-header-text">
            <span>批评方向</span>
            <p class="preview-note">
              勾选要生成的方向；可在表格中直接编辑（不截断）。共 {{ directions.length }} 条，已选 {{ selectedCount }} 条。正文将并发生成。
            </p>
          </div>
          <div class="header-actions">
            <el-button size="small" link type="primary" :disabled="!directions.length" @click="selectAll">
              全选
            </el-button>
            <el-button size="small" link :disabled="!directions.length" @click="selectNone">
              清空
            </el-button>
          </div>
        </div>
      </template>

      <el-table
        v-if="directions.length"
        ref="dirTableRef"
        :data="directions"
        row-key="_key"
        size="small"
        stripe
        border
      >
        <el-table-column width="52" align="center">
          <template #header>
            <el-checkbox
              :model-value="allDirectionsSelected"
              :indeterminate="directionsIndeterminate"
              @change="toggleSelectAllDirections"
            />
          </template>
          <template #default="{ row }">
            <el-checkbox v-model="row.selected" />
          </template>
        </el-table-column>
        <el-table-column label="#" type="index" width="50" />
        <el-table-column label="方向简称" width="180">
          <template #default="{ row }">
            <el-input v-model="row.label" size="small" placeholder="如：文化工业批判" />
          </template>
        </el-table-column>
        <el-table-column label="批评核心方向" min-width="320">
          <template #default="{ row }">
            <el-input
              v-model="row.text"
              type="textarea"
              :rows="3"
              size="small"
              placeholder="核心论点 / 调用的理论资源 / 可切入的案例方向（完整保留，不截断）"
            />
          </template>
        </el-table-column>
        <el-table-column label="WebSearch 关键词" min-width="240">
          <template #default="{ row }">
            <el-input
              v-model="row.search_query"
              type="textarea"
              :rows="3"
              size="small"
              placeholder="拆方向时模型已给出；可手动修改。留空则自动用「主题 + 方向 + 理论 + 案例」拼接"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="72" fixed="right">
          <template #default="{ $index }">
            <el-button link type="danger" size="small" @click="removeDirection($index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-empty v-else description="填写主题后点击「AI 拆方向」，或「添加自定义方向」" />
    </el-card>

    <!-- 任务预览 -->
    <el-card v-if="previewJobs.length > 0" class="preview-card">
      <template #header>
        <div class="card-header">
          <div class="preview-header-text">
            <span>任务预览（前 {{ Math.min(previewJobs.length, 10) }} 条）</span>
            <p class="preview-note">
              每条选中方向将生成一篇<strong>专栏</strong>口播稿，并写入<strong>任务队列</strong>与<strong>文章管理</strong>（CO）。
            </p>
          </div>
          <el-tag>共 {{ previewJobs.length }} 篇</el-tag>
        </div>
      </template>
      <el-table :data="previewJobs.slice(0, 10)" size="small" stripe>
        <el-table-column label="#" type="index" width="50" />
        <el-table-column label="主题" min-width="200" show-overflow-tooltip>
          <template #default="{ row }">
            <span class="preview-name">{{ row.topic_short }}</span>
          </template>
        </el-table-column>
        <el-table-column label="批评方向" min-width="200">
          <template #default="{ row }">
            <el-tag size="small" type="warning">{{ row.direction_label }}</el-tag>
            <span class="preview-name">{{ row.direction_preview }}</span>
          </template>
        </el-table-column>
        <el-table-column label="WebSearch 关键词" min-width="180" show-overflow-tooltip>
          <template #default="{ row }">
            <span class="preview-name">{{ row.search_query_preview }}</span>
          </template>
        </el-table-column>
        <el-table-column label="平台" width="88">
          <template #default>
            <el-tag size="small" type="info">专栏</el-tag>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 生成结果 -->
    <el-card
      v-if="genJob.articles.length || genJob.errors.length || genJob.status === 'running'"
      class="preview-card"
    >
      <template #header>
        <div class="card-header">
          <span>生成结果</span>
          <el-tag v-if="genJob.status === 'running'" type="warning">进行中 {{ genJob.done }}/{{ genJob.total }}</el-tag>
          <el-tag v-else-if="genJob.status === 'completed'" type="success">已完成</el-tag>
        </div>
      </template>
      <el-table v-if="genJob.articles.length" :data="genJob.articles" size="small" stripe>
        <el-table-column prop="article_id" label="ID" width="72" />
        <el-table-column label="方向" width="160">
          <template #default="{ row }">
            <span>{{ extractDirLabel(row.label) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" min-width="240" show-overflow-tooltip />
        <el-table-column label="解析" width="92">
          <template #default="{ row }">
            <el-tag v-if="row.parse_ok === false" size="small" type="danger">JSON 失败</el-tag>
            <el-tag v-else size="small" type="success">{{ row.references_count || 0 }} refs</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220">
          <template #default="{ row }">
            <el-button link type="primary" @click="openArticle(row.article_id)">查看</el-button>
            <el-button link type="primary" @click="goTasks">任务队列</el-button>
            <el-button link type="primary" @click="goHistory">文章管理</el-button>
          </template>
        </el-table-column>
      </el-table>
      <ul v-if="genJob.errors.length" class="error-list">
        <li v-for="(e, i) in genJob.errors" :key="i">{{ e.label }}：{{ e.error }}</li>
      </ul>
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onActivated, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Plus, Refresh } from '@element-plus/icons-vue'
import { columnOralApi } from '../api'

let _keySeq = 0
function nextKey() {
  _keySeq += 1
  return `k_${_keySeq}`
}

export default {
  name: 'ColumnOral',
  setup() {
    const router = useRouter()
    const dirTableRef = ref(null)
    const form = reactive({
      topic: '',
      theory: '',
      cases: '',
      extraHint: '',
      runSearch: true,
      searchBudget: 6,
      articlePromptKey: '',
    })
    const directions = ref([])
    const loadingDirections = ref(false)
    const generating = ref(false)

    // 写正文 System 变体
    const articlePromptOptions = ref([
      { key: '', name: '默认（写口播稿 System）', is_default: true, source: 'builtin' },
    ])
    const loadingArticlePrompts = ref(false)

    const loadArticlePrompts = async () => {
      loadingArticlePrompts.value = true
      try {
        const res = await columnOralApi.getArticlePrompts()
        const items = (res?.items || []).filter(Boolean)
        if (items.length) {
          articlePromptOptions.value = items
          const exists = items.some((it) => (it.key || '') === (form.articlePromptKey || ''))
          if (!exists) form.articlePromptKey = ''
        }
      } catch (e) {
        ElMessage.warning('加载写正文模板列表失败：' + (e?.message || ''))
      } finally {
        loadingArticlePrompts.value = false
      }
    }

    const genJob = reactive({
      taskId: null,
      status: '',
      total: 0,
      done: 0,
      articles: [],
      errors: [],
    })
    let pollTimer = null

    const selectedCount = computed(
      () => directions.value.filter((d) => d.selected && (d.text || '').trim()).length,
    )

    const allDirectionsSelected = computed(() => {
      const list = directions.value
      if (!list.length) return false
      return list.every((d) => d.selected)
    })

    const directionsIndeterminate = computed(() => {
      const list = directions.value
      if (!list.length) return false
      const n = list.filter((d) => d.selected).length
      return n > 0 && n < list.length
    })

    const previewJobs = computed(() => {
      const topic = form.topic.trim()
      if (!topic) return []
      return directions.value
        .filter((d) => d.selected && (d.text || '').trim())
        .map((d) => ({
          topic_short: topic.length > 48 ? topic.slice(0, 48) + '…' : topic,
          direction_label: d.label || '未命名',
          direction_preview:
            (d.text || '').length > 40 ? (d.text || '').slice(0, 40) + '…' : d.text,
          search_query_preview: ((d.search_query || '').length > 40
            ? (d.search_query || '').slice(0, 40) + '…'
            : (d.search_query || '')) || '(自动拼接)',
        }))
    })

    const genProgress = computed(() => {
      if (!genJob.total) return 0
      return Math.round((genJob.done / genJob.total) * 100)
    })

    const toggleSelectAllDirections = (checked) => {
      directions.value.forEach((d) => {
        d.selected = !!checked
      })
    }

    const fetchDirections = async () => {
      if (!form.topic.trim()) return
      loadingDirections.value = true
      try {
        const res = await columnOralApi.generateDirections({
          topic: form.topic.trim(),
          theory: form.theory.trim(),
          cases: form.cases.trim(),
          extra_hint: form.extraHint.trim(),
        })
        const list = (res.directions || []).map((d) => ({
          _key: nextKey(),
          id: d.id,
          label: d.label || '',
          text: d.text || '',
          search_query: d.search_query || '',
          selected: true,
        }))
        if (!list.length) {
          ElMessage.warning('未解析到批评方向，请重试或点击「添加自定义方向」')
        } else {
          directions.value = list
          ElMessage.success(`已生成 ${list.length} 条批评方向，请勾选后生成口播稿`)
        }
      } catch (e) {
        const msg = e?.message || ''
        const hint = msg.includes('timeout') ? '（拆方向超时，已放宽超时，请重试）' : ''
        ElMessage.error('拆方向失败：' + msg + hint)
      } finally {
        loadingDirections.value = false
      }
    }

    const addCustomDirection = () => {
      if (!form.topic.trim()) {
        ElMessage.warning('请先填写主题')
        return
      }
      directions.value.push({
        _key: nextKey(),
        id: `custom_${Date.now()}`,
        label: '自定义方向',
        text: '',
        search_query: '',
        selected: true,
      })
    }

    const removeDirection = (idx) => {
      directions.value.splice(idx, 1)
    }

    const selectAll = () => {
      toggleSelectAllDirections(true)
    }

    const selectNone = () => {
      toggleSelectAllDirections(false)
    }

    const resetForm = () => {
      form.topic = ''
      form.theory = ''
      form.cases = ''
      form.extraHint = ''
      form.runSearch = true
      form.searchBudget = 6
      form.articlePromptKey = ''
      directions.value = []
      genJob.taskId = null
      genJob.status = ''
      genJob.total = 0
      genJob.done = 0
      genJob.articles = []
      genJob.errors = []
      if (pollTimer) clearTimeout(pollTimer)
    }

    const pollStatus = () => {
      if (pollTimer) clearTimeout(pollTimer)
      const tick = async () => {
        try {
          const s = await columnOralApi.getGenerateStatus(genJob.taskId)
          genJob.status = s.status
          genJob.total = s.total ?? s.total_count ?? 0
          genJob.done = s.done ?? ((s.completed_count || 0) + (s.failed_count || 0))
          genJob.articles = s.articles || []
          genJob.errors = s.errors || []
          if (['completed', 'cancelled', 'failed'].includes(s.status)) {
            generating.value = false
            const ok = s.completed_count ?? genJob.articles.length
            ElMessage.success(`生成结束：成功 ${ok} 篇，可在任务队列查看详情`)
            return
          }
          pollTimer = setTimeout(tick, 2000)
        } catch (e) {
          generating.value = false
          ElMessage.error('查询进度失败')
        }
      }
      tick()
    }

    const startGenerate = async () => {
      const selected = directions.value.filter(
        (d) => d.selected && (d.text || '').trim(),
      )
      if (!selected.length) {
        const checked = directions.value.filter((d) => d.selected).length
        if (checked > 0) {
          ElMessage.warning('已勾选的方向需填写「批评核心方向」说明（不能为空）')
        } else {
          ElMessage.warning('请至少勾选一条方向，并填写批评核心方向')
        }
        return
      }
      generating.value = true
      genJob.articles = []
      genJob.errors = []
      genJob.status = 'running'
      try {
        const jobs = selected.map((d) => ({
          id: d.id,
          topic: form.topic.trim(),
          direction: (d.text || '').trim(),
          theory: form.theory.trim(),
          cases: form.cases.trim(),
          extra_hint: form.extraHint.trim(),
          run_search: !!form.runSearch,
          search_budget: form.searchBudget,
          search_query: (d.search_query || '').trim(),
          article_prompt_key: form.articlePromptKey || '',
          label: `${form.topic.trim().slice(0, 24)} || ${(d.label || d.text || '').slice(0, 24)}`,
        }))
        const res = await columnOralApi.startGenerate({ jobs })
        genJob.taskId = res.task_id ?? Number(res.job_id)
        genJob.status = 'pending'
        genJob.total = res.total ?? jobs.length
        genJob.done = 0
        ElMessage.info(`任务 #${genJob.taskId} 已加入任务队列`)
        pollStatus()
      } catch (e) {
        generating.value = false
        genJob.status = ''
        ElMessage.error('启动生成失败：' + (e?.message || ''))
      }
    }

    const goHistory = () => {
      router.push('/content/library')
    }

    const goTasks = () => {
      router.push('/content/tasks')
    }

    const openArticle = (articleId) => {
      router.push({ path: '/content/library', query: { open: articleId } })
    }

    const extractDirLabel = (label) => {
      if (!label) return ''
      const parts = String(label).split('||')
      if (parts.length >= 2) return parts.slice(1).join('||').trim()
      return label
    }

    onMounted(() => {
      loadArticlePrompts()
    })
    onActivated(() => {
      loadArticlePrompts()
    })
    onBeforeUnmount(() => {
      if (pollTimer) clearTimeout(pollTimer)
    })

    return {
      dirTableRef,
      form,
      directions,
      loadingDirections,
      generating,
      genJob,
      selectedCount,
      previewJobs,
      genProgress,
      articlePromptOptions,
      loadingArticlePrompts,
      loadArticlePrompts,
      fetchDirections,
      addCustomDirection,
      removeDirection,
      selectAll,
      selectNone,
      resetForm,
      allDirectionsSelected,
      directionsIndeterminate,
      toggleSelectAllDirections,
      startGenerate,
      goHistory,
      goTasks,
      openArticle,
      extractDirLabel,
      Plus,
      Refresh,
    }
  },
}
</script>

<style scoped>
.column-oral {
  padding: 0;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.preview-header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.preview-note {
  margin: 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.45;
  max-width: 720px;
}

.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.hint-text {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}

.count-hint {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
}

.directions-card {
  margin-top: 20px;
}

.preview-card {
  margin-top: 20px;
}

.preview-name {
  margin-left: 6px;
  font-size: 12px;
  color: #606266;
}

.error-list {
  color: #f56c6c;
  font-size: 13px;
  margin: 12px 0 0;
  padding-left: 18px;
}
</style>
